/**
 * Pure step-navigation helpers for the Wizard, kept free of React so they are unit-testable.
 *
 * Two kinds of steps never show up in the progress bar:
 *
 *  - `$` steps ($BANK_CONFIRMATION, ...) are invisible pass-through effects. Going forward VISITS them
 *    so they run; going backward skips them so the applicant never lands on one.
 *  - hidden steps are real steps the applicant will not see right now (PROVE_STEP once Prove consent is
 *    written, BUSINESS_SELECT while there is no consent). They are passed over in BOTH directions and
 *    never mount, so Back cannot land on a step that immediately moves forward again. They are named by
 *    the parent (LoanAppForm) because only the parent knows why they would be skipped.
 */

// Strip the $ marker so analytics events report the clean step name.
export const stripStepMarker = (step: string) => (step.startsWith('$') ? step.slice(1) : step);

export const isPassThroughStep = (step: unknown) => String(step).includes('$');

export const isHiddenStep = (step: unknown, hiddenSteps: readonly string[] = []) =>
  !isPassThroughStep(step) && hiddenSteps.includes(stripStepMarker(String(step)));

/** Steps that are not counted or shown in the progress bar. */
export const isInvisibleStep = (step: unknown, hiddenSteps: readonly string[] = []) =>
  isPassThroughStep(step) || isHiddenStep(step, hiddenSteps);

export const getVisibleSteps = <T>(stepOrder: readonly T[], hiddenSteps: readonly string[] = []): T[] =>
  stepOrder.filter((step) => !isInvisibleStep(step, hiddenSteps));

/**
 * Position of the current step among the visible steps (what the progress bar and isFirstStep use).
 * An invisible current step gets a fractional position between its visible neighbours, and a step
 * that is not in the order at all (APP_REVIEW, DECLINED, ...) gets -1, which hides the bar.
 */
export const calcStepIndex = (stepOrder: string[], currentStep: string, hiddenSteps: readonly string[] = []) => {
  const invisible = (step: string) => isInvisibleStep(step, hiddenSteps);
  const filteredSteps = stepOrder.filter((step) => !invisible(step));
  const currentStepIndex = filteredSteps.indexOf(currentStep);

  if (currentStepIndex !== -1) {
    return currentStepIndex;
  }

  const previousSteps = stepOrder.slice(0, stepOrder.indexOf(currentStep)).reverse();
  const nextSteps = stepOrder.slice(stepOrder.indexOf(currentStep) + 1);
  const previousValidStepIndex = previousSteps.findIndex((step) => !invisible(step));
  const nextValidStepIndex = nextSteps.findIndex((step) => !invisible(step));

  const surroundingSteps = stepOrder.slice(
    stepOrder.indexOf(currentStep) - previousValidStepIndex - 1,
    stepOrder.indexOf(currentStep) + nextValidStepIndex + 1
  );

  const placeholderSteps = surroundingSteps.filter((step) => invisible(step));
  const placeholderIndex = placeholderSteps.indexOf(currentStep);

  return (1 / (placeholderSteps.length + 1)) * (placeholderIndex + 1) + filteredSteps.indexOf(
    stepOrder[stepOrder.indexOf(currentStep) - previousValidStepIndex - 1]
  );
};

/** Index of the step to move to from `currentIndex`, or -1 when the order has none in that direction. */
export const findTargetIndex = (
  stepOrder: readonly unknown[],
  currentIndex: number,
  direction: 'next' | 'previous',
  hiddenSteps: readonly string[] = [],
) => {
  const increment = direction === 'next' ? 1 : -1;
  let index = currentIndex + increment;

  while (
    index >= 0 &&
    index < stepOrder.length &&
    (isHiddenStep(stepOrder[index], hiddenSteps) || (direction === 'previous' && isPassThroughStep(stepOrder[index])))
  ) {
    index += increment;
  }

  return index >= 0 && index < stepOrder.length ? index : -1;
};
